import React from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from '@/components/common/Sidebar';
import Header from '@/components/common/Header';
import { GlobalTicketNotifier } from '@/components/common/GlobalTicketNotifier';
import { useThemeStore, useAuthStore } from '@/stores';
import { refreshAccessToken, isTokenExpiringSoon } from '@/utils/auth';
import { useLogout } from '@/hooks/useAuth';
import { AlertTriangle } from 'lucide-react';

const MainLayout: React.FC = () => {
  const { sidebarOpen, language, theme, toggleSidebar, setSidebarOpen } = useThemeStore();
  const { logoutConfirmOpen, setLogoutConfirmOpen } = useAuthStore();
  const { confirmLogout, isLoading: isLoggingOut } = useLogout();
  const isRTL = language === 'ar';
  const isLight = theme === 'light';

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    
    // Default closed sidebar on mobile on initial render
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarOpen]);

  // Silent background token refresh to keep connection active continuously
  React.useEffect(() => {
    const checkAndRefresh = async () => {
      const { accessToken } = useAuthStore.getState();
      if (accessToken) {
        // If the token is expiring in the next 2 minutes (120 seconds), refresh it
        if (isTokenExpiringSoon(accessToken, 120)) {
          console.log('Background auth: Access token expiring soon, refreshing...');
          await refreshAccessToken();
        }
      }
    };

    // Check immediately on mount
    checkAndRefresh();

    // Check every 30 seconds
    const intervalId = setInterval(checkAndRefresh, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      {/* Mobile safe-area CSS variables injection */}
      <style>{`
        :root {
          --safe-area-bottom: env(safe-area-inset-bottom, 0px);
          --safe-area-top: env(safe-area-inset-top, 0px);
        }
        .main-layout-root {
          height: 100vh;
          width: 100%;
        }
        .main-scroll-area {
          /* Extra padding accounts for iOS Safari bottom bar + safe area */
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
        }
        @media (max-width: 768px) {
          #root {
            height: 100dvh !important;
          }
          .main-layout-root {
            height: 100dvh !important;
            width: 100vw !important;
          }
        }
      `}</style>
      <div
        className="main-layout-root"
        style={{
          display: 'flex',
          background: isLight ? '#f1f5f9' : '#060f20',
          direction: isRTL ? 'rtl' : 'ltr',
          flexDirection: isRTL ? 'row' : 'row-reverse',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Mobile backdrop */}
        {isMobile && sidebarOpen && (
          <div
            onClick={toggleSidebar}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(2px)',
              zIndex: 1000,
            }}
          />
        )}
        {/* Sidebar - always on right in RTL */}
        <Sidebar />

        {/* Main area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Header />
          <main
            className="main-scroll-area"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch', // smooth momentum scrolling on iOS
              boxSizing: 'border-box',
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>

      {/* Logout confirmation dialog */}
      {logoutConfirmOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(5, 11, 23, 0.5)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.2s ease-out',
          }}
        >
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          `}</style>
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.85)',
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '380px',
              boxShadow: isLight ? '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(16px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              animation: 'scaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {/* Warning Icon Container */}
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                color: '#ef4444',
                border: '1px dashed rgba(239, 68, 68, 0.3)',
              }}
            >
              <AlertTriangle size={28} />
            </div>

            {/* Content */}
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: isLight ? '#1e293b' : '#f8fafc',
                margin: '0 0 8px 0',
              }}
            >
              هل أنت متأكد؟
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: isLight ? '#64748b' : '#94a3b8',
                margin: '0 0 24px 0',
                lineHeight: '1.5',
              }}
            >
              هل أنت متأكد من رغبتك في تسجيل الخروج من التطبيق؟
            </p>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                width: '100%',
              }}
            >
              <button
                onClick={() => setLogoutConfirmOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                  background: isLight ? '#f8fafc' : 'rgba(30, 41, 59, 0.5)',
                  color: isLight ? '#475569' : '#cbd5e1',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(30, 41, 59, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isLight ? '#f8fafc' : 'rgba(30, 41, 59, 0.5)';
                }}
              >
                إلغاء
              </button>
              <button
                onClick={confirmLogout}
                disabled={isLoggingOut}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#ef4444',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#dc2626';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(220, 38, 38, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ef4444';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                }}
              >
                {isLoggingOut ? 'جاري الخروج...' : 'نعم، خروج'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Continuous Global Ticket Push Notifier */}
      <GlobalTicketNotifier />
    </>
  );
};

export default MainLayout;
